import React, { useMemo, useRef, useState } from "react";

import { ColDef, GetRowIdParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Modal } from "antd";
import i18next from "i18next";
import { toast } from "react-toastify";

import { useGetAllDistributorsForPromoProduct, useUpsertPromoProductDistributor } from "api/reactQuery/queryComponents";
import {
  BSCoreServicesDtoMarketplacePromoProductDistributorDto,
  BSCoreServicesDtoMarketplaceProductDistributorDto,
  BSCoreServicesDtoMarketplaceProductDto,
} from "api/reactQuery/querySchemas";
import { getGridDefaultProps } from "constants/agGridConstants";

type Props = {
  isOpen: boolean;
  toggleModalOpen: () => void;
  productId: BSCoreServicesDtoMarketplaceProductDto["id"];
};

const OnInvoiceConfigModal: React.FC<Props> = ({
  isOpen,
  toggleModalOpen,
  productId,
}) => {
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const { data } = useGetAllDistributorsForPromoProduct(
    { queryParams: { productId }, },
    { enabled: !!productId && isOpen },
  );
  const gridRef = useRef<AgGridReact<BSCoreServicesDtoMarketplacePromoProductDistributorDto>>(null);

  const { mutate } = useUpsertPromoProductDistributor({
    onError: () => {
      toast.error(i18next.t("something_went_wrong"));
    },
    onSuccess: () => {
      toast.success("Configuration updated successfully");
      toggleModalOpen();
    },
  });

  const disabledCellStyle = { color: "rgba(0, 0, 0, 0.5)" };
  const editableCellStyle = { fontWeight: "500" };

  const columnDefs = useMemo<ColDef<BSCoreServicesDtoMarketplacePromoProductDistributorDto>[]>(() =>
    [
      {
        field: "distributorName",
        flex: 2,
        headerName: i18next.t("distributor"),
      },
      {
        field: "distributorDepotName",
        flex: 2,
        headerName: i18next.t("depot"),
      },
      {
        cellStyle: (node) => node.data.isInvoiceLimitEditable ? editableCellStyle : disabledCellStyle,
        editable: (node) => node.data.isInvoiceLimitEditable,
        field: "onInvoiceLimit",
        headerName: i18next.t("on_invoice_limit"),
      },
      {
        cellStyle: (node) => node.data.isBonusOnInvoiceLimitEditable ? editableCellStyle : disabledCellStyle,
        editable: (node) => node.data.isBonusOnInvoiceLimitEditable,
        field: "bonusOnInvoiceLimit",
        headerName: i18next.t("bonus_on_invoice_limit"),
      },
      {
        cellStyle: (node) => node.data.isNcpCompensationLimitEditable ? editableCellStyle : disabledCellStyle,
        editable: (node) => node.data.isNcpCompensationLimitEditable,
        field: "ncpCompensationLimit",
        headerName: i18next.t("ncp_compensation_limit"),
      },
    ],
    [],
  );

  const onSaveHandler = () => {
    const allRowData: BSCoreServicesDtoMarketplaceProductDistributorDto[] = [];
    gridRef?.current?.api?.forEachNode((node) => allRowData.push(node.data));

    allRowData.length &&
      mutate({ body: allRowData, queryParams: { productId } });
  };

  return (
    <Modal
      open={isOpen}
      centered
      destroyOnHidden
      afterOpenChange={(open) => !open && setIsSaveDisabled(true)}
      okText="Save"
      onOk={onSaveHandler}
      okButtonProps={{ disabled: isSaveDisabled }}
      onCancel={toggleModalOpen}
      width="50%"
      styles={{ body: { height: "70vh" } }}
      title={i18next.t("limits_configuration")}
    >
      <AgGridReact
        ref={gridRef}
        onCellValueChanged={() => setIsSaveDisabled(false)}
        getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplacePromoProductDistributorDto>,) => String(params.data.distributorId)}
        rowData={data}
        domLayout="normal"
        singleClickEdit
        columnDefs={columnDefs}
        {...getGridDefaultProps()}
      />
    </Modal>
  );
};

export default OnInvoiceConfigModal;
